import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from 'components/UI/V3/Pagination/Size/PaginationSize.module.scss';
import React from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_OPTIONS = [10, 20, 50];

interface PaginationSizeProps {
    size: number;
    options?: Array<number>;
    onChange?: (size: number) => void;
}

export const PaginationSize: React.FC<PaginationSizeProps> = ({ size, options = DEFAULT_OPTIONS, onChange }) => {
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    const [isOpen, setIsOpen] = React.useState(false);

    const sizeChangeHandler = React.useCallback(
        (option: number) => {
            onChange?.(option);
            setIsOpen(false);
        },
        [onChange]
    );

    const dropdownPosition = React.useMemo(() => {
        if (!buttonRef.current || !isOpen) {
            return { bottom: 0, left: 0 };
        }

        const buttonRect = buttonRef.current.getBoundingClientRect();
        const estimatedDropdownHeight = 120; // Estimate for 3 options with padding
        const spaceBelow = window.innerHeight - buttonRect.bottom;

        const showBelow = spaceBelow >= estimatedDropdownHeight;

        if (showBelow) {
            // Position dropdown below the button
            return {
                right: window.innerWidth - buttonRect.right,
                top: buttonRect.bottom + 8
            };
        } else {
            // Position dropdown above the button
            return {
                right: window.innerWidth - buttonRect.right,
                bottom: window.innerHeight - buttonRect.top + 8
            };
        }
    }, [isOpen]);

    return (
        <>
            <button
                type="button"
                ref={buttonRef}
                className={styles.root}
                onClick={() => setIsOpen(!isOpen)}
            >
                View {size}
                <FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} />
            </button>

            {isOpen &&
                createPortal(
                    <div
                        className={styles.dropdown}
                        style={dropdownPosition}
                    >
                        {options.map(option => (
                            <div
                                key={option}
                                className={styles.dropdownItem}
                                onClick={() => sizeChangeHandler(option)}
                            >
                                View {option}
                            </div>
                        ))}
                    </div>,
                    document.body
                )}
        </>
    );
};
